import { FileInput, FileInputLabel, Icon } from '@vp/swan';
import React, { useContext } from 'react';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { util } from '../../utils/miaw/common';
import { getDeploymentDeveloperName } from '../../utils/miaw/chat-config';
import { IMessagePayload } from '../../interfaces/component-interfaces';

export interface IUploadAttachmentButtonProps {
  handleFileChange: (data: IMessagePayload[]) => void;
  attachmentMessage?: string;
  disabled?: boolean;
}

const allowedExtensions = ['.bmp', '.csv', '.doc', '.docx', '.gif', '.jpg', '.jpeg', '.pdf', '.png', '.tiff', '.txt', '.xls', '.xlsx', '.xml'];

const allowedMimeTypes: Record<string, string> = {
  '.bmp': 'image/bmp',
  '.csv': 'text/csv',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.gif': 'image/gif',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.tiff': 'image/tiff',
  '.txt': 'text/plain',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xml': 'application/xml',
};

const UploadAttachmentButton: React.FC<IUploadAttachmentButtonProps> = ({ handleFileChange, attachmentMessage, disabled }) => {
  const {
    setState: setMIAWState,
    state: { conversationId },
  } = useContext(MiawChatContext);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMIAWState((prev) => ({
      ...prev,
      attachmentErrorMessage: '',
      showAttachmentError: false,
    }));
    const files = event.target.files;
    const payload: IMessagePayload[] = [];
    const errorFiles: string[] = [];
    const unsupportedFiles: string[] = [];
    const MAX_FILES = 5;

    if (!files || files.length === 0) return;

    const selectedFiles = Array.from(files);

    if (selectedFiles.length > MAX_FILES) {
      setMIAWState((prev) => ({
        ...prev,
        attachmentErrorMessage: `You can only upload ${MAX_FILES} files. You selected ${selectedFiles.length}.`,
        showAttachmentError: true,
      }));
    }

    for (const file of selectedFiles) {
      if (payload.length >= MAX_FILES) break;

      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type;

      const isValidExtension = allowedExtensions.includes(extension);
      const isValidMime = allowedMimeTypes[extension] === mimeType;

      if (!isValidExtension || !isValidMime) {
        unsupportedFiles.push(file.name);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        errorFiles.push(file.name);
        continue;
      }

      const messageEntry = {
        message: {
          id: util.generateUUID(),
          fileId: util.generateUUID(),
          text: selectedFiles.length === 1 ? attachmentMessage || '' : '',
          inReplyToMessageId: conversationId,
        },
        esDeveloperName: getDeploymentDeveloperName(),
        isNewMessagingSession: false,
      };

      payload.push({
        conversationId: conversationId || '',
        messageEntry,
        fileData: file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (errorFiles.length || unsupportedFiles.length) {
      const truncateNames = (names: string[]) =>
        names.map((name) => {
          const dotIndex = name.lastIndexOf('.');
          const baseName = dotIndex !== -1 ? name.substring(0, dotIndex) : name;
          return baseName.length > 10 ? baseName.substring(0, 10) + '…' : baseName;
        });

      const sizeMessage = errorFiles.length
        ? `${truncateNames(errorFiles).join(', ')} ${errorFiles.length === 1 ? 'is' : 'are'} too large. Upload a file smaller than 5MB.`
        : '';

      const formatMessage = unsupportedFiles.length
        ? `${truncateNames(unsupportedFiles).join(', ')} ${
            unsupportedFiles.length === 1 ? 'is' : 'are'
          } unsupported. Allowed formats: ${allowedExtensions.join(', ')}.`
        : '';

      setMIAWState((prev) => ({
        ...prev,
        attachmentErrorMessage: formatMessage ? formatMessage : sizeMessage ? sizeMessage : '',
        showAttachmentError: true,
      }));
    }

    handleFileChange(payload);
    event.target.value = '';
  };
  return (
    <FileInput onChange={onChange} multiple disabled={disabled}>
      <FileInputLabel style={{ boxShadow: 'none', width: '36px', height: '36px' }}>
        <Icon iconType="attachment" />
      </FileInputLabel>
    </FileInput>
  );
};

export default UploadAttachmentButton;
